/**
 * The page-permission tree: the checkbox list of every screen a user can be
 * granted.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A SHARED COMPONENT
 *
 * There were two different pickers for one job. «إعادة تعيين الصفحة» drew this
 * tree, grouped and expandable; the create-user form drew a flat
 * `MultipleSelect` of the same rows with the grouping lost - and on EDIT it
 * drew nothing at all, so the only way to change a user's pages was to close
 * the form and reopen the other modal.
 *
 * One component now serves create, edit and reassign, so the three can no
 * longer drift apart and the list is the same list in all of them.
 *
 * ---------------------------------------------------------------------------
 * THE LIST IS THE SIDEBAR
 *
 * `pagesData` is `GET /api/RolePage/GetRolePages` - the same rows, from the
 * same table, that produce each operator's menu. So every screen in the sidebar
 * appears here and can be switched on or off, which is the property the whole
 * feature rests on.
 *
 * ---------------------------------------------------------------------------
 * WHAT WAS WRONG WITH THE ORIGINAL TREE
 *
 * `toggleParent` looked its parent up in the FLAT array and then read
 * `parent.children` - a property only the built tree has. It was always
 * `undefined`, `[].every(...)` is `true`, so EVERY tick or untick of a child
 * ticked its parent. Unchecking the last child of a group left the group
 * checked, and the group's name was then saved as a granted page.
 *
 * This version walks the built tree, and a parent is checked only when all of
 * its children are.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';

import styles from './UserRoleModal.module.css';
import chevronDown from '../../../../assets/Imagess/chevron-down.svg';

/** Collapse the `\r\n` prefixes and double spaces the backend sends in `name`. */
export const cleanLabel = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const labelOf = (row) => cleanLabel(row?.name || row?.pageName);

/** Flat rows -> nested tree, ordered the way the menu is ordered. */
export const buildPagesTree = (pagesData, parentId = null) =>
  (Array.isArray(pagesData) ? pagesData : [])
    .filter((page) => (page?.parentId ?? null) === parentId)
    .sort((a, b) => (a?.orderIndex ?? 0) - (b?.orderIndex ?? 0))
    .map((page) => ({
      ...page,
      children: buildPagesTree(pagesData, page.pageId),
    }));

/** Every `pageId` at or under `node`. */
const idsUnder = (node) => {
  const ids = [node.pageId];
  (node.children ?? []).forEach((child) => ids.push(...idsUnder(child)));
  return ids;
};

/**
 * Which pages does this user already hold?
 *
 * `userRoles` is ONE comma-joined string of page NAMES - the only shape the
 * users endpoint returns - so the match is by label, and both sides are folded
 * through `cleanLabel` first because the backend prefixes several names with a
 * literal `\r\n`.
 *
 * @returns {Set<string>} pageIds
 */
export const selectionFromUserRoles = (userRoles, pagesData) => {
  const names = new Set(
    String(userRoles ?? '')
      .split(',')
      .map(cleanLabel)
      .filter(Boolean)
  );

  const selected = new Set();
  (Array.isArray(pagesData) ? pagesData : []).forEach((page) => {
    if (names.has(labelOf(page))) selected.add(page.pageId);
  });
  return selected;
};

/**
 * One row, and its children when it is open.
 *
 * DECLARED AT MODULE LEVEL, not inside `PagesTree`. A component defined in a
 * render body is a NEW component type on every render, so React unmounts and
 * remounts the entire subtree each time anything changes - every tick of a box
 * threw away and rebuilt all 28 rows. Hoisting it makes them ordinary updates.
 */
const TreeItem = ({
  node,
  level,
  selected,
  expanded,
  onCheck,
  onToggleExpand,
}) => {
  const isChecked = selected.has(node.pageId);
  const hasChildren = !isEmpty(node.children);
  const isExpanded = expanded.has(node.pageId);

  return (
    <div
      className={styles['tree-item']}
      style={{ marginLeft: level > 0 ? 20 * level : 0 }}
    >
      <div
        className={`${styles['tree-row']} ${
          level === 0 ? styles['parent'] : styles['child']
        }`}
        onClick={() => hasChildren && onToggleExpand(node)}
      >
        <div className={styles.labelContainer}>
          <input
            type='checkbox'
            checked={isChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onCheck(node, e.target.checked)}
          />
          <span className={styles['label']}>{labelOf(node)}</span>
        </div>

        {hasChildren && (
          <div
            className={styles['arrow-div']}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node);
            }}
          >
            <img
              src={chevronDown}
              alt=''
              className={`${styles['arrow']} ${
                isExpanded ? styles['open'] : ''
              }`}
            />
          </div>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className={styles.children}>
          {node.children.map((child) => (
            <TreeItem
              key={child.pageId}
              node={child}
              level={level + 1}
              selected={selected}
              expanded={expanded}
              onCheck={onCheck}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PagesTree = ({ pagesData, selected, onChange, label }) => {
  const [expanded, setExpanded] = useState(() => new Set());

  const tree = useMemo(() => buildPagesTree(pagesData), [pagesData]);

  const allIds = useMemo(
    () => (Array.isArray(pagesData) ? pagesData.map((p) => p.pageId) : []),
    [pagesData]
  );

  /**
   * Tick or untick a node, its whole subtree, and then re-derive every ancestor.
   *
   * Ancestors are RE-DERIVED rather than toggled: a group is checked exactly
   * when all of its children are. That is the rule that the original code got
   * backwards, and deriving it leaves no state that can disagree with the
   * boxes on screen.
   */
  const handleCheck = useCallback(
    (node, isChecked) => {
      const next = new Set(selected);

      idsUnder(node).forEach((id) => {
        if (isChecked) next.add(id);
        else next.delete(id);
      });

      const refreshGroups = (rows) =>
        rows.forEach((row) => {
          if (isEmpty(row.children)) return;
          refreshGroups(row.children); // depth first - children settle first
          const allChecked = row.children.every((child) => next.has(child.pageId));
          if (allChecked) next.add(row.pageId);
          else next.delete(row.pageId);
        });

      refreshGroups(tree);
      onChange(next);
    },
    [selected, tree, onChange]
  );

  const toggleExpand = useCallback((node) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(node.pageId)) next.delete(node.pageId);
      else next.add(node.pageId);
      return next;
    });
  }, []);

  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  return (
    <div className={styles['tree-container']}>
      {/* Only the create/edit form passes a label — the reassign modal's own
          title already says what the tree is. */}
      {label && <span className={styles['tree-label']}>{label}</span>}

      <div className={styles['select-all-container']}>
        <input
          type='checkbox'
          checked={allSelected}
          onChange={(e) => onChange(new Set(e.target.checked ? allIds : []))}
        />
        <span className={styles['label']}>الكل</span>
      </div>

      {tree.map((node) => (
        <TreeItem
          key={node.pageId}
          node={node}
          level={0}
          selected={selected}
          expanded={expanded}
          onCheck={handleCheck}
          onToggleExpand={toggleExpand}
        />
      ))}

      {/* Granting nothing is a legitimate choice - it is how an account is
          suspended without deleting it - but it must not be a silent one. The
          operator is told exactly what that user will see. */}
      {selected.size === 0 && (
        <p className={styles['empty-hint']}>
          لم يتم تحديد أي صفحة. سيظهر للمستخدم إشعار بعدم وجود صلاحية عند تسجيل
          الدخول.
        </p>
      )}
    </div>
  );
};

export default PagesTree;
